import { type ReactElement, cloneElement } from 'react';

import { useDataTable } from '../../contexts/DataTableContext.js';
import type { DataTableRowData } from '../../public.api.js';
import type { DataTableSelectedRowsActionsProps } from './DataTableSelectedRowsActions.js';
import type { DataTableTableActionsProps } from './DataTableTableActions.js';

type DataTableActionsWrapperProps<TData extends DataTableRowData> = {
  tableActions: ReactElement<DataTableTableActionsProps> | null;
  selectedRowsActions: ReactElement<
    DataTableSelectedRowsActionsProps<TData>
  > | null;
};

/**
 * This component is used to conditionally render TableActions and SelectedRowsActions
 * and toggle their visibility state
 * @internal
 */
export const DataTableTableActionsWrapper = <TData extends DataTableRowData>(
  props: DataTableActionsWrapperProps<TData>,
): ReactElement | null => {
  const { tableActions, selectedRowsActions } = props;
  const table = useDataTable();
  const showSelectedRowsActions =
    selectedRowsActions && table.getSelectionCount() > 0;

  if (tableActions === null && selectedRowsActions === null) {
    return null;
  }

  return (
    <>
      {selectedRowsActions &&
        cloneElement(selectedRowsActions, {
          style: {
            visibility: showSelectedRowsActions ? 'visible' : 'hidden',
            gridArea: ' 1 / 1 / 2 / 2',
            ...selectedRowsActions.props.style,
          },
        })}
      {tableActions &&
        cloneElement(tableActions, {
          style: {
            visibility:
              !showSelectedRowsActions && tableActions ? 'visible' : 'hidden',
            gridArea: ' 1 / 1 / 2 / 2',
            ...tableActions.props.style,
          },
        })}
    </>
  );
};
